import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Chip,
  TextField,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  Add,
  Remove,
  Delete,
  ShoppingCart,
  LocalShipping,
  ShoppingBag,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { getImageProps } from '../../utils/imageUtils';
import { CartItemSkeleton } from '../common/SkeletonLoader';

const CartDrawer = ({ 
  open, 
  onClose, 
  cart: propCart, 
  onUpdateQuantity, 
  onRemoveItem, 
  onCheckout, 
  loading = false,
  checkoutButtonText = "Checkout",
  checkoutButtonIcon = <LocalShipping />
}) => {
  const navigate = useNavigate();
  const {
    cart: contextCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartItemCount,
    restaurant,
  } = useRestaurant();

  // Use prop cart if provided, otherwise use context cart
  const cart = propCart || contextCart;
  
  const cartTotal = getCartTotal ? getCartTotal() : 0;
  const cartItemCount = getCartItemCount ? getCartItemCount() : 0;
  const deliveryFee = cartTotal > 500 ? 0 : 50;
  const taxes = Math.round(cartTotal * 0.18);
  const finalTotal = cartTotal + deliveryFee + taxes;

  const handleQuantityChange = (cartItemId, newQuantity) => {
    if (onUpdateQuantity) {
      onUpdateQuantity(cartItemId, newQuantity);
    } else if (newQuantity <= 0) {
      removeFromCart && removeFromCart(cartItemId);
    } else {
      updateCartItem && updateCartItem(cartItemId, { quantity: newQuantity });
    }
  };

  const handleRemoveItem = (cartItemId) => {
    if (onRemoveItem) {
      onRemoveItem(cartItemId);
    } else {
      removeFromCart && removeFromCart(cartItemId);
    }
  };

  const handleCheckout = (serviceType) => {
    if (onCheckout) {
      onCheckout(serviceType);
    } else {
      onClose();
      if (serviceType === 'guest-signup') {
        navigate('/guest-signup');
      } else {
        navigate('/checkout', { state: { orderType: serviceType } });
      }
    }
  };

  if (!cart || cart.length === 0) {
    return (
      <Drawer 
        anchor="right" 
        open={open} 
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '90vw', sm: 380 },
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            size="small"
          >
            <Close sx={{ fontSize: '1.4rem' }} />
          </IconButton>
          
          <ShoppingCart sx={{ fontSize: 60, color: 'text.secondary', mt: 3, mb: 1.5 }} />
          <Typography variant="h6" gutterBottom sx={{ fontSize: '14px' }}>
            Your cart is empty
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '14px' }}>
            Add some delicious items to get started
          </Typography>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ borderRadius: 2, fontSize: '14px', py: 1 }}
          >
            Continue Shopping
          </Button>
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer 
      anchor="right" 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '90vw', sm: 380 },
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="600" sx={{ fontSize: '14px' }}>
              Your Order ({cartItemCount} {cartItemCount === 1 ? 'item' : 'items'})
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Close sx={{ fontSize: '1.4rem' }} />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
            from {restaurant?.name || 'our restaurant'}
          </Typography>
        </Box>

        {/* Cart Items */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Box sx={{ p: 1 }}>
            {loading ? (
              <CartItemSkeleton count={3} />
            ) : (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { 
                  xs: 'repeat(2, 1fr)', 
                  sm: '1fr' 
                },
                gap: { xs: 1, sm: 0.5 }
              }}>
                {(cart || []).map((item) => (
                  <Card key={item?.id} sx={{ 
                    my: { xs: 0, sm: 0.5 }, 
                    boxShadow: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}>
                    <CardMedia
                      component="img"
                      sx={{ 
                        width: { xs: '100%', sm: 70 }, 
                        height: { xs: 120, sm: 70 }, 
                        objectFit: 'cover' 
                      }}
                      {...getImageProps(item?.image_url, 'menu_item', item?.name)}
                    />
                    <CardContent sx={{ 
                      flexGrow: 1, 
                      p: { xs: 1, sm: 1.5 }, 
                      '&:last-child': { pb: { xs: 1, sm: 1.5 } },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        mb: { xs: 1, sm: 0 }
                      }}>
                        <Box sx={{ flexGrow: 1, pr: 1 }}>
                          <Typography variant="body2" fontWeight="600" gutterBottom sx={{ 
                            fontSize: { xs: '0.75rem', sm: '14px' },
                            lineHeight: 1.2
                          }}>
                            {item?.name || 'Menu Item'}
                          </Typography>
                          
                          {item?.selectedSize && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ 
                              fontSize: { xs: '0.7rem', sm: '14px' }
                            }}>
                              Size: {item.selectedSize.name || item.selectedSize}
                            </Typography>
                          )}
                          
                          {item?.addons && item.addons.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              {item.addons.slice(0, 2).map((addon, index) => (
                                <Chip
                                  key={index}
                                  label={`${addon.name}${addon.quantity > 1 ? ` (${addon.quantity})` : ''}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ 
                                    mr: 0.5, 
                                    mb: 0.5, 
                                    fontSize: { xs: '0.65rem', sm: '14px' }, 
                                    height: { xs: 16, sm: 20 }
                                  }}
                                />
                              ))}
                              {item.addons.length > 2 && (
                                <Typography variant="caption" color="text.secondary" sx={{ 
                                  fontSize: { xs: '0.65rem', sm: '12px' }
                                }}>
                                  +{item.addons.length - 2} more
                                </Typography>
                              )}
                            </Box>
                          )}
                          
                          {item?.special_instructions && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ 
                              mt: 0.5, 
                              fontSize: { xs: '0.7rem', sm: '14px' },
                              lineHeight: 1.2
                            }}>
                              Note: {item.special_instructions}
                            </Typography>
                          )}
                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'flex-end', 
                          gap: 0.5,
                          minWidth: { xs: '60px', sm: 'auto' }
                        }}>
                          <Typography variant="body2" fontWeight="600" color="primary.main" sx={{ 
                            fontSize: { xs: '0.75rem', sm: '14px' }
                          }}>
                            ${item?.price || item?.base_price || 0}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(item.id, (item.quantity || 1) - 1)}
                              sx={{ 
                                bgcolor: 'grey.100', 
                                '&:hover': { bgcolor: 'grey.200' },
                                width: { xs: 20, sm: 24 },
                                height: { xs: 20, sm: 24 }
                              }}
                            >
                              <Remove sx={{ fontSize: { xs: '1rem', sm: '1.4rem' } }} />
                            </IconButton>
                            
                            <Typography variant="body2" sx={{ 
                              minWidth: 16, 
                              textAlign: 'center', 
                              fontSize: { xs: '0.75rem', sm: '14px' }
                            }}>
                              {item.quantity || 1}
                            </Typography>
                            
                            <IconButton
                              size="small"
                              onClick={() => handleQuantityChange(item.id, (item.quantity || 1) + 1)}
                              sx={{ 
                                bgcolor: 'grey.100', 
                                '&:hover': { bgcolor: 'grey.200' },
                                width: { xs: 20, sm: 24 },
                                height: { xs: 20, sm: 24 }
                              }}
                            >
                              <Add sx={{ fontSize: { xs: '1rem', sm: '1.4rem' } }} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mt: { xs: 1, sm: 0.5 }
                      }}>
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.7rem', sm: '14px' }
                        }}>
                          Total: ${((item?.price || item?.base_price || 0) * (item.quantity || 1)).toFixed(2)}
                        </Typography>
                        
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveItem(item.id)}
                          color="error"
                          sx={{ 
                            bgcolor: 'error.50', 
                            '&:hover': { bgcolor: 'error.100' },
                            width: { xs: 20, sm: 24 },
                            height: { xs: 20, sm: 24 }
                          }}
                        >
                          <Delete sx={{ fontSize: { xs: '1rem', sm: '1.4rem' } }} />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Order Summary */}
        <Paper elevation={3} sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontSize: '14px' }}>
            Order Summary
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontSize: '14px' }}>Subtotal</Typography>
            <Typography variant="body2" sx={{ fontSize: '14px' }}>${cartTotal.toFixed(2)}</Typography>
          </Box>
          
          {/* Discount Savings */}
          {(() => {
            const totalSavings = cart.reduce((sum, item) => {
              if (item.discount_enabled && item.discount_percentage > 0) {
                const originalTotal = item.original_price * item.quantity;
                const discountedTotal = item.discounted_price * item.quantity;
                return sum + (originalTotal - discountedTotal);
              }
              return sum;
            }, 0);
            
            return totalSavings > 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="success.main" sx={{ fontSize: '14px' }}>Discount Savings</Typography>
                <Typography variant="body2" color="success.main" fontWeight="600" sx={{ fontSize: '14px' }}>
                  -${totalSavings.toFixed(2)}
                </Typography>
              </Box>
            ) : null;
          })()}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontSize: '14px' }}>Delivery Fee</Typography>
            <Typography variant="body2" color={deliveryFee === 0 ? 'success.main' : 'inherit'} sx={{ fontSize: '14px' }}>
              {deliveryFee === 0 ? 'FREE' : `$${deliveryFee.toFixed(2)}`}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontSize: '14px' }}>Taxes & Charges</Typography>
            <Typography variant="body2" sx={{ fontSize: '14px' }}>${taxes.toFixed(2)}</Typography>
          </Box>
          
          <Divider sx={{ my: 1.5 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '14px' }}>
              To Pay
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ fontSize: '14px' }}>
              ${finalTotal.toFixed(2)}
            </Typography>
          </Box>

          {cartTotal > 0 && cartTotal < 30 && (
            <Typography variant="caption" color="warning.main" sx={{ mb: 1.5, display: 'block', textAlign: 'center', fontSize: '14px' }}>
              Add S${(30 - cartTotal).toFixed(2)} more for free delivery
            </Typography>
          )}

          {/* Checkout Button */}
          <Button
            variant="contained"
            fullWidth
            startIcon={<ShoppingCart sx={{ fontSize: '1.4rem' }} />}
            onClick={() => {
              if (onCheckout) {
                onCheckout('guest-signup');
              } else {
                handleCheckout('guest-signup');
              }
            }}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              height: 40,
              fontSize: '14px',
              py: 1
            }}
          >
            {loading ? 'Processing...' : 'Proceed to Checkout'}
          </Button>

          <Button
            variant="text"
            fullWidth
            onClick={clearCart && clearCart}
            color="error"
            sx={{ textTransform: 'none', fontSize: '14px', py: 0.5 }}
          >
            Clear Cart
          </Button>
        </Paper>
      </Box>
    </Drawer>
  );
};

export default CartDrawer;
